import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Icon, Tooltip, Text, Flex, ButtonSelect } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import './AINotesDisplay.scss';
import { useTranslation } from 'Components/Helpers/translate';

interface AINotesDisplayProps {
	notes?: string;
	notesEn?: string;
	onChange: (field: 'aiNotesEn' | 'aiNotes', value: string) => void;
	disabled?: boolean;
}

const AINotesDisplay = ({ notes = '', notesEn = '', onChange, disabled = false }: AINotesDisplayProps) => {
	const classes = new BemClass('AINotesDisplay');
	const { t } = useTranslation();
	const [selectedLanguage, setSelectedLanguage] = useState('original');
	const [localNotes, setLocalNotes] = useState(notes);
	const [localNotesEn, setLocalNotesEn] = useState(notesEn);

	// Only show toggle if we have both notes (i.e., user language is not English)
	const hasEnglishVersion = notesEn && notesEn !== notes;
	const notesToShow = selectedLanguage === 'english' ? localNotesEn : localNotes;
	const field = selectedLanguage === 'english' ? 'aiNotesEn' : 'aiNotes';

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Update local state when props change
	useEffect(() => {
		setLocalNotes(notes);
	}, [notes]);

	useEffect(() => {
		setLocalNotesEn(notesEn);
	}, [notesEn]);

	const debouncedOnChange = useCallback(
		(field: 'aiNotesEn' | 'aiNotes', value: string) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(() => {
				onChange(field, value);
			}, 500);
		},
		[onChange]
	);

	const handleTextChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.target.value;

			// Update local state immediately for responsive UI
			if (selectedLanguage === 'english') {
				setLocalNotesEn(value);
			} else {
				setLocalNotes(value);
			}

			// Debounce the save operation
			debouncedOnChange(field, value);
		},
		[selectedLanguage, field, debouncedOnChange]
	);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	if (!notes) return null;

	return (
		<div className={classes.b()}>
			<Flex flex={1} className={classes.elem('textareaContainer').b()}>
				<div className={classes.elem('header').b()}>
					<div className={classes.elem('headerLeft').b()}>
						<Text size="md" space="mrm" color="black">
							{t('default.aiSummary')}
						</Text>
						<Tooltip title={t('notes.internalClarification')}>
							<Icon name="question-circle" color="black" />
						</Tooltip>
					</div>
					{hasEnglishVersion ? (
						<Flex gap="u1" alignItems="center">
							<ButtonSelect
								size="sm"
								value={selectedLanguage}
								onChange={setSelectedLanguage}
								options={[
									{
										value: 'original',
										title: t('default.original')
									},
									{
										value: 'english',
										title: 'English'
									}
								]}
							/>
						</Flex>
					) : null}
				</div>
				<textarea
					value={notesToShow}
					onChange={handleTextChange}
					rows={6}
					className={classes.elem('textarea').b()}
					disabled={disabled}
				/>
			</Flex>
		</div>
	);
};

export default AINotesDisplay;
